import { httpClient } from '../client';
import { FlagBag, FlagBagStatusResponse, OfflineData, FlagBagListResponse, UpdateFlagBagRequest, UpdateFlagBagResponse, BatchSyncFlagBagRequest, BatchSyncFlagBagResponse, SingleFlagBagResponse, FlagBagStatusListResponse } from './types';

export * from './types';

export class FlagBagApi {
  // 获取单页数据
  async getFlagBagPage(eventId: string, stationId: string, pageNo: number = 1, pageSize: number = 50, _search?: string): Promise<FlagBagListResponse['data']> {
    // Validate required parameters
    if (!eventId || !stationId) {
      throw {
        errorCode: -1,
        errorMsg: `Missing required parameters: ${!eventId ? 'eventId' : ''} ${!stationId ? 'stationId' : ''}`.trim(),
      };
    }

    const params: any = { 
      eventId, 
      stationId,
      pageSize,
      pageNo,
      decrypted:true
    };

    const pageData = await httpClient.get<FlagBagListResponse['data']>('/flagbageasy/flagbag/', params);

    return pageData;
  }

  async getFlagBagList(eventId: string, stationId: string, pageNo: number, pageSize: number, _search?: string): Promise<FlagBagListResponse['data']> {
    // Validate required parameters
    if (!eventId || !stationId) {
      throw {
        errorCode: -1,
        errorMsg: `Missing required parameters: ${!eventId ? 'eventId' : ''} ${!stationId ? 'stationId' : ''}`.trim(),
      };
    }

    const baseParams: any = { 
      eventId, 
      stationId,
      pageSize,
      pageNo
    };

    baseParams.includeOvercollected = true;

    // Fetch first page to get total count
    // httpClient.get already extracts the 'data' field from IResponse
    const firstPageData = await httpClient.get<FlagBagListResponse['data']>('/flagbageasy/flagbag/', baseParams);

    const total = firstPageData.total;
    const items = [...firstPageData.items];

    // If there are more items, fetch remaining pages
    if (total > items.length) {
      const totalPages = Math.ceil(total / pageSize);
      const remainingPages = [];

      for (let pageNo = 2; pageNo <= totalPages; pageNo++) {
        const pageParams: any = { 
          eventId, 
          stationId,
          pageSize,
          pageNo,
          includeOvercollected: true,
        };
        remainingPages.push(
          httpClient.get<FlagBagListResponse['data']>('/flagbageasy/flagbag/', pageParams)
        );
      }

      const results = await Promise.all(remainingPages);
      results.forEach(result => {
        items.push(...result.items);
      });
    }

    return {
      total: total,
      unassignedTotal: firstPageData.unassignedTotal,
      items: items
    };
  }

  async getStatus(flagBagID: string, stationID: string): Promise<FlagBagStatusResponse> {
    return httpClient.get<FlagBagStatusResponse>('/getFlagBagStatus', {
      flagBagID,
      stationID,
    });
  }

  async updateSingle(
    flagBagId: string,
    eventId: string,
    stationId: string,
    payload: UpdateFlagBagRequest
  ): Promise<UpdateFlagBagResponse> {
    return httpClient.patch<UpdateFlagBagResponse>(`/flagbageasy/flagbag/${flagBagId}`,
      {
        ...payload,
      },
      {
        eventId,
        stationId,
      }
    );
  }

  async checkOutSpare(
    stationID: string,
    flagBagID: string,
    name: string,
    phone: string,
  ): Promise<any> {
    return httpClient.legacyGet('/checkOutSpareFlagBag', {
      flagBagID,
      stationID,
      name,
      phone,
    });
  }

  async flushOfflineData(
    stationID: string,
    data: OfflineData[],
    timestamp: number
  ): Promise<any> {
    return httpClient.legacyGet('/updateOfflineData', {
      stationID,
      data: JSON.stringify(data),
      timestamp,
    });
  }

  async batchSyncFlagBag(
    eventId: string,
    payload: BatchSyncFlagBagRequest
  ): Promise<BatchSyncFlagBagResponse> {
    return httpClient.post<BatchSyncFlagBagResponse>(`/flagbageasy/flagbag/sync-statuses`, {
      logs: payload.logs,
      updates: payload.updates,
    }, {
      eventId,
    });
  }

  async getSingleFlagBag(eventId: string, flagBagId: string): Promise<SingleFlagBagResponse> {
    return httpClient.get<SingleFlagBagResponse>(`/flagbageasy/flagbag/${flagBagId}`, {
      eventId,
    });
  }

  async getStatuses(eventId: string): Promise<FlagBagStatusListResponse['data']> {
    if (!eventId) {
      throw {
        errorCode: -1,
        errorMsg: 'Missing required parameter: eventId',
      };
    }

    return httpClient.get<FlagBagStatusListResponse['data']>('/flagbageasy/flagbag/statuses', {
      eventId,
    });
  }
}

export const flagBagApi = new FlagBagApi();
